import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getUserData, saveUserData } from '../api/client';
import { useAuth } from './AuthContext';

const PetContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8103/api/v1';

// Ignore SSE events for this many ms after we push (to avoid re-fetching our own echo)
const PUSH_ECHO_IGNORE_MS = 3000;

export function PetProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [userData, setUserData] = useState(null);
  const [pets, setPets] = useState([]);
  const [activePet, setActivePet] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, synced, error
  const pollRef = useRef(null);
  const lastPushTime = useRef(0); // timestamp of last push - used to ignore SSE echo

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) return;
    setSyncStatus('syncing');
    try {
      const res = await getUserData();
      const data = res.data;
      if (!data) {
        // No user data yet (new account) - start fresh
        setUserData({ pets: [] });
        setPets([]);
        setActivePet(null);
        setSyncStatus('synced');
        return;
      }
      // Normalize camelCase fields from API to have both formats available
      const normalizedPets = (data.pets || []).map(normalizePet);
      setUserData({ ...data, pets: normalizedPets });
      setPets(normalizedPets);
      const active = normalizedPets.find((p) => p.active);
      setActivePet(active || null);
      setSyncStatus('synced');
    } catch (err) {
      // Sync failed silently - status shown in UI
      setSyncStatus('error');
    }
  }, [isAuthenticated]);

  // Initial fetch + SSE for real-time updates (replaces polling)
  useEffect(() => {
    if (!isAuthenticated) {
      setUserData(null);
      setPets([]);
      setActivePet(null);
      setSyncStatus('idle');
      return;
    }

    // Initial fetch
    fetchData();

    // Connect to SSE for real-time push
    // Use withCredentials to send httpOnly cookie. Fall back to query param
    // for environments where cookies aren't available (mod web view).
    const token = localStorage.getItem('petlings_token');
    let eventSource = null;

    try {
      // EventSource doesn't support cookies natively in all browsers,
      // so we still pass token as query param fallback
      const sseUrl = token
        ? `${API_BASE}/events/stream?token=${token}`
        : `${API_BASE}/events/stream`;
      eventSource = new EventSource(sseUrl, { withCredentials: true });

      eventSource.addEventListener('connected', () => {
        setSyncStatus('synced');
      });

      eventSource.addEventListener('data_changed', () => {
        // Skip re-fetch if we just pushed (this is our own echo)
        const timeSincePush = Date.now() - lastPushTime.current;
        if (timeSincePush < PUSH_ECHO_IGNORE_MS) {
          return; // ignore - we already have the latest data locally
        }
        // External change (from mod or another device) - re-fetch
        fetchData();
      });

      eventSource.addEventListener('heartbeat', () => {
        // Keep-alive, nothing to do
      });

      eventSource.onerror = () => {
        setSyncStatus('error');
        // EventSource auto-reconnects
      };
    } catch {
      // SSE not supported or connection failed - fall back to polling
      pollRef.current = setInterval(fetchData, 30000);
    }

    return () => {
      if (eventSource) eventSource.close();
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isAuthenticated, fetchData]);

  const pushUpdate = useCallback(async (updatedPets) => {
    try {
      lastPushTime.current = Date.now(); // mark push time to ignore SSE echo
      // Clean pets to camelCase only (backend expects alias format)
      const cleanPets = updatedPets.map(p => ({
        petTypeId: p.pet_type_id || p.petTypeId,
        petName: p.pet_name || p.petName,
        traitIds: p.trait_ids || p.traitIds || [],
        slotIndex: p.slot_index ?? p.slotIndex ?? 0,
        active: p.active || false,
        tiny: p.tiny || false,
        variantId: p.variant_id || p.variantId || null,
        tintColor: p.tint_color ?? p.tintColor ?? 0,
        level: p.level || 1,
        xp: p.xp || 0,
        hunger: (p.hunger ?? 1) <= 1 ? p.hunger * 100 : p.hunger,
        happiness: (p.happiness ?? 1) <= 1 ? p.happiness * 100 : p.happiness,
        energy: (p.energy ?? 1) <= 1 ? p.energy * 100 : p.energy,
        mood: p.mood || 'CONTENT',
        equippedCosmetics: Object.fromEntries(
          Object.entries(p.equipped_cosmetics || p.equippedCosmetics || {})
            .filter(([, v]) => v != null && v !== '')
        ),
        cosmeticPositions: p.cosmetic_positions || p.cosmeticPositions || {},
        emoteShortcuts: p.emote_shortcuts || p.emoteShortcuts || [],
        adoptedAt: p.adopted_at || p.adoptedAt || 0,
        lastActiveAt: p.last_active_at || p.lastActiveAt || 0,
        lastModifiedAt: Date.now(),
      }));
      const newData = { ...userData, pets: cleanPets };
      setUserData({ ...newData, pets: updatedPets }); // keep normalized locally
      await saveUserData(newData); // send clean camelCase to backend
    } catch (err) {
      // Push failed - will retry on next action
      setSyncStatus('error');
      fetchData();
    }
  }, [userData, fetchData]);

  const adoptPet = useCallback(async (typeId, name, traits) => {
    // Find first empty slot
    const usedSlots = pets.map((p) => p.slot_index);
    let newSlot = 0;
    while (usedSlots.includes(newSlot) && newSlot < 10) newSlot++;
    if (newSlot >= 10) return { success: false, error: 'All pet slots are full.' };

    const newPet = {
      slot_index: newSlot,
      active: pets.length === 0,
      pet_type_id: typeId,
      pet_name: name,
      trait_ids: traits || ['curious'],
      tiny: false,
      variant_id: null,
      tint_color: 0,
      level: 1,
      xp: 0,
      hunger: 1.0,
      happiness: 1.0,
      energy: 1.0,
      mood: 'happy',
      equipped_cosmetics: {},
      cosmetic_positions: {},
      emote_shortcuts: [],
      adopted_at: Date.now(),
      last_active_at: Date.now(),
      last_modified_at: Date.now(),
    };

    const updatedPets = [...pets, newPet];
    setPets(updatedPets);
    if (newPet.active) setActivePet(newPet);
    await pushUpdate(updatedPets);
    return { success: true, pet: newPet };
  }, [pets, pushUpdate]);

  const releasePet = useCallback(async (slot) => {
    const updatedPets = pets.filter((p) => p.slot_index !== slot);
    setPets(updatedPets);
    if (activePet?.slot_index === slot) {
      setActivePet(updatedPets[0] || null);
    }
    await pushUpdate(updatedPets);
  }, [pets, activePet, pushUpdate]);

  const equipCosmetic = useCallback(async (slot, cosmeticSlot, cosmeticId) => {
    const updatedPets = pets.map((p) => {
      if (p.slot_index !== slot) return p;
      return {
        ...p,
        equipped_cosmetics: { ...p.equipped_cosmetics, [cosmeticSlot]: cosmeticId },
        last_modified_at: new Date().toISOString(),
      };
    });
    setPets(updatedPets);
    await pushUpdate(updatedPets);
  }, [pets, pushUpdate]);

  const saveCosmeticPositions = useCallback(async (slot, positions, transforms) => {
    // Merge positions + transforms into a single dict: { cosmeticId: { x, y, rotation, scale } }
    const merged = {};
    for (const [cosmeticId, pos] of Object.entries(positions || {})) {
      merged[cosmeticId] = { ...pos, ...(transforms?.[cosmeticId] || {}) };
    }
    // Also include transforms for items without custom positions (rotation/scale only)
    for (const [cosmeticId, t] of Object.entries(transforms || {})) {
      if (!merged[cosmeticId]) {
        merged[cosmeticId] = { rotation: t.rotation || 0, scale: t.scale || 1.0 };
      }
    }
    const updatedPets = pets.map((p) => {
      if (p.slot_index !== slot) return p;
      return {
        ...p,
        cosmetic_positions: merged,
        last_modified_at: new Date().toISOString(),
      };
    });
    setPets(updatedPets);
    await pushUpdate(updatedPets);
  }, [pets, pushUpdate]);

  const updatePetStats = useCallback(async (slot, changes) => {
    const updatedPets = pets.map((p) => {
      if (p.slot_index !== slot) return p;
      return { ...p, ...changes, last_modified_at: new Date().toISOString() };
    });
    setPets(updatedPets);
    const active = updatedPets.find((p) => p.active);
    if (active) setActivePet(active);
    await pushUpdate(updatedPets);
  }, [pets, pushUpdate]);

  const feedPet = useCallback(async (slot) => {
    const pet = pets.find((p) => p.slot_index === slot);
    if (!pet) return;
    const newHunger = Math.min(1.0, pet.hunger + 0.25);
    const newHappiness = Math.min(1.0, pet.happiness + 0.05);
    const newXp = pet.xp + 10;
    const newLevel = Math.floor(newXp / 100) + 1;
    await updatePetStats(slot, {
      hunger: newHunger,
      happiness: newHappiness,
      xp: newXp,
      level: newLevel,
      mood: computeMood(newHunger, newHappiness, pet.energy),
    });
  }, [pets, updatePetStats]);

  const playWithPet = useCallback(async (slot) => {
    const pet = pets.find((p) => p.slot_index === slot);
    if (!pet) return;
    const newHappiness = Math.min(1.0, pet.happiness + 0.3);
    const newEnergy = Math.max(0, pet.energy - 0.15);
    const newHunger = Math.max(0, pet.hunger - 0.1);
    const newXp = pet.xp + 15;
    const newLevel = Math.floor(newXp / 100) + 1;
    await updatePetStats(slot, {
      happiness: newHappiness,
      energy: newEnergy,
      hunger: newHunger,
      xp: newXp,
      level: newLevel,
      mood: computeMood(newHunger, newHappiness, newEnergy),
    });
  }, [pets, updatePetStats]);

  const restPet = useCallback(async (slot) => {
    const pet = pets.find((p) => p.slot_index === slot);
    if (!pet) return;
    const newEnergy = Math.min(1.0, pet.energy + 0.35);
    const newXp = pet.xp + 5;
    const newLevel = Math.floor(newXp / 100) + 1;
    await updatePetStats(slot, {
      energy: newEnergy,
      xp: newXp,
      level: newLevel,
      mood: computeMood(pet.hunger, pet.happiness, newEnergy),
    });
  }, [pets, updatePetStats]);

  return (
    <PetContext.Provider value={{
      userData,
      pets,
      activePet,
      syncStatus,
      adoptPet,
      releasePet,
      equipCosmetic,
      saveCosmeticPositions,
      updatePetStats,
      feedPet,
      playWithPet,
      restPet,
      refreshData: fetchData,
    }}>
      {children}
    </PetContext.Provider>
  );
}

/**
 * Normalize pet data to have both camelCase and snake_case fields,
 * so components can use either format consistently.
 */
function normalizePet(pet) {
  // Backend stores needs on 0-100 scale, frontend uses 0-1.
  // Detect and normalize: if any need value > 1, it's on the 0-100 scale.
  const h = pet.hunger ?? 100;
  const ha = pet.happiness ?? 100;
  const e = pet.energy ?? 100;
  const needsScale100 = h > 1 || ha > 1 || e > 1;
  const hunger = needsScale100 ? h / 100 : h;
  const happiness = needsScale100 ? ha / 100 : ha;
  const energy = needsScale100 ? e / 100 : e;

  return {
    ...pet,
    // Normalized needs (always 0-1)
    hunger: Math.max(0, Math.min(1, hunger)),
    happiness: Math.max(0, Math.min(1, happiness)),
    energy: Math.max(0, Math.min(1, energy)),
    // Ensure both formats exist
    pet_type_id: pet.pet_type_id || pet.petTypeId,
    pet_name: pet.pet_name || pet.petName,
    slot_index: pet.slot_index ?? pet.slotIndex ?? 0,
    variant_id: pet.variant_id || pet.variantId,
    tint_color: pet.tint_color ?? pet.tintColor ?? 0,
    trait_ids: pet.trait_ids || pet.traitIds || [],
    equipped_cosmetics: pet.equipped_cosmetics || pet.equippedCosmetics || {},
    cosmetic_positions: pet.cosmetic_positions || pet.cosmeticPositions || {},
    emote_shortcuts: pet.emote_shortcuts || pet.emoteShortcuts || [],
    adopted_at: pet.adopted_at || pet.adoptedAt,
    last_active_at: pet.last_active_at || pet.lastActiveAt,
    last_modified_at: pet.last_modified_at || pet.lastModifiedAt,
    // Keep camelCase too
    petTypeId: pet.petTypeId || pet.pet_type_id,
    petName: pet.petName || pet.pet_name,
    slotIndex: pet.slotIndex ?? pet.slot_index ?? 0,
    variantId: pet.variantId || pet.variant_id,
    equippedCosmetics: pet.equippedCosmetics || pet.equipped_cosmetics || {},
    cosmeticPositions: pet.cosmeticPositions || pet.cosmetic_positions || {},
  };
}

function computeMood(hunger, happiness, energy) {
  if (hunger < 0.2) return 'hungry';
  if (energy < 0.2) return 'sleepy';
  if (happiness < 0.3) return 'sad';
  if (happiness > 0.7 && energy > 0.5) return 'happy';
  if (energy > 0.8) return 'energetic';
  return 'neutral';
}

export function usePets() {
  const ctx = useContext(PetContext);
  if (!ctx) throw new Error('usePets must be used within PetProvider');
  return ctx;
}

export default PetContext;
